import {
	Binoculars,
	CaretCircleLeft,
	CaretDown,
	CaretLeft,
	CaretRight,
	GraduationCap,
	Warning,
} from '@phosphor-icons/react';
import PrimaryButton from '../../components/atoms/Button';
import Text from '../../components/atoms/Text';
import { useNavigate, useSearchParams, useBlocker } from 'react-router-dom';
import ROUTES from '../../shared/constants/routes';
import { useEffect, useState } from 'react';
import Step1 from './organism/Step1';
import Step2 from './organism/Step2';
import Step3 from './organism/Step3';
import {
	getUserDeclarationDetail,
	getUserDeclarationDetailFFT,
	putUserDeclarationStatus,
} from '../../services/flowApproval.service';
import {
	ApprovalDeclarationDetail,
	ApprovalDeclarationDetailFFT,
	Detail,
} from '../../@types/Declarations.type';
import StepsAnnual from '../annualDeclaration/components/StepsAnnual';
import { getFiscalAnnualPreview } from '../../services/tax.service';
import Modal from '../../components/molecules/Modal/Modal';
import { getCompleteMonth } from '../../shared/utils/dates.util';
import { showAlert } from '../../redux/slices/alert.slice';
import { ALERT_TYPE } from '../../components/molecules/Alert';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import { sendEvent } from '../../services/user-interaction.service';
import { ANALYTICS_EVENTS, EVENT_TYPES } from '../../shared/constants/events';
import useClarityEvent from '../../hooks/useClarityEvent';

const ApprovalDeclarationsSteps = () => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const fftId = searchParams.get('fftId');
	const viewHome = searchParams.get('viewHome');
	const viewLucy = searchParams.get('viewLucy');
	const dispatch = useDispatch();
	const userProfile = useSelector((state: RootState) => state.profile.user);
	const { trackEvent } = useClarityEvent();
	const [currentStep, setCurrentStep] = useState(1);
	const [isOpenPersonales, setIsOpenPersonales] = useState(false);
	const [declarationDetail, setDeclarationDetail] =
		useState<ApprovalDeclarationDetail>();
	const [monthPreviewData, setMonthPreviewData] = useState<any>();
	const [declarationDetailFFT, setDeclarationDetailFFT] =
		useState<ApprovalDeclarationDetailFFT>();
	const [showReviewModal, setShowReviewModal] = useState(false);
	const [viewModal, setViewModal] = useState(false);
	const [selectedOption, setSelectedOption] = useState<string>('');
	const [reasonText, setReasonText] = useState('');
	const [validationError, setValidationError] = useState(false);

	const blocker = useBlocker(
		({ currentLocation, nextLocation }) =>
			(currentStep === 2 || currentStep === 3) &&
			currentLocation.pathname !== nextLocation.pathname &&
			!showReviewModal &&
			!viewModal,
	);

	const backApprovalDeclarations = () => {
		if (viewHome === 'true') navigate(ROUTES.ROOT);
		else if (viewLucy === 'true') navigate(ROUTES.SERVICE_STATUS);
		else navigate(ROUTES.TAXES);
	};

	const handleStepClick = (step: number) => {
		setCurrentStep(step);
	};

	const handleNext = async () => {
		if (currentStep === 1) {
			await approvedExpenseAndDeductions();
		} else if (currentStep === 2) {
			await approvedExpenseAndDeductions();
		}
		setCurrentStep((prev) => prev + 1);
	};

	const handleBack = () => {
		if (currentStep > 1) {
			setCurrentStep(currentStep - 1);
		}
	};

	const handleSendToReview = () => {
		setShowReviewModal(true);
		reviewDeclaration();
	};

	const handleModalSubmit = () => {
		if (!selectedOption || !reasonText) {
			setValidationError(true);
			return;
		}

		putDeclarationStatus(Number(declarationDetailFFT?.id));
		sendReviewDeclaration();
		setShowReviewModal(false);
		navigate(ROUTES.TAXES);
	};

	const incomes = declarationDetail?.income.find(
		(income) => 'platec_income' in income,
	)?.platec_income;
	const expenses = declarationDetail?.expense.find(
		(expense) => 'total_expense' in expense,
	)?.total_expense;
	const deductibles = declarationDetail?.deductibles.find(
		(deductible) => 'based_deductibles' in deductible,
	)?.based_deductibles;

	const renderStepContent = () => {
		switch (currentStep) {
			case 1:
				return (
					<Step1
						retentionIncomes={incomes?.retentionIncome}
						month={getCompleteMonth(
							Number(declarationDetail?.precalculate_details?.[0]?.month),
						)}
					/>
				);
			case 2:
				return (
					<Step2
						user_id={userProfile?.id}
						deductibles={deductibles}
						expenses={expenses}
						month={getCompleteMonth(
							Number(declarationDetail?.precalculate_details?.[0]?.month),
						)}
					/>
				);
			case 3:
				return (
					<Step3
						user_id={userProfile?.id}
						viewModal={setViewModal}
						id={Number(declarationDetailFFT?.id)}
						amount={Number(
							declarationDetail?.precalculate_details?.[0].totalBalance,
						)}
						precalculate_details={
							declarationDetail?.precalculate_details[0] as Detail
						}
					/>
				);
			default:
				return null;
		}
	};

	const putDeclarationStatus = async (id: number) => {
		try {
			await putUserDeclarationStatus(id, {
				status: 'denied',
				reason_denied: [
					{
						reason: reasonText,
						option: selectedOption,
						resume_ai: false,
					},
				],
			});
		} catch (error) {
			const alert = {
				type: ALERT_TYPE.ERROR,
				title: 'Error',
				description: 'Error al enviar a revisión',
			};
			dispatch(showAlert(alert));
		}
	};

	const getDeclarationDetail = async (fftId: number) => {
		try {
			const response = await getUserDeclarationDetail(fftId);
			setDeclarationDetail(response);
		} catch (error) {}
	};

	const getMonthPreviewData = async (month: string, year: string) => {
		try {
			const response = await getFiscalAnnualPreview(year, month);
			setMonthPreviewData(response);
		} catch (error) {}
	};

	const getDeclarationDetailFFT = async (fftId: number) => {
		try {
			const response = await getUserDeclarationDetailFFT(fftId);
			setDeclarationDetailFFT(response);
		} catch (error) {}
	};

	const approvedExpenseAndDeductions = async () => {
		if (!userProfile?.id) return;

		try {
			if (currentStep === 1) {
				await sendEvent({
					user_id: userProfile.id,
					event: ANALYTICS_EVENTS.APPROVAL_DECLARATIONS,
					source: 'web',
					event_payload: {
						subsection: EVENT_TYPES.APPROVAL_DECLARATIONS_APPROVED_EXPENSE,
					}
				});
				trackEvent(EVENT_TYPES.APPROVAL_DECLARATIONS_APPROVED_EXPENSE);	
			} else if (currentStep === 2) {
				await sendEvent({
					user_id: userProfile.id,
					event: ANALYTICS_EVENTS.APPROVAL_DECLARATIONS,
					source: 'web',
					event_payload: {
						subsection: EVENT_TYPES.APPROVAL_DECLARATIONS_APPROVED_DEDUCTIONS,
					},
				});
				trackEvent(EVENT_TYPES.APPROVAL_DECLARATIONS_APPROVED_DEDUCTIONS);
			}
		} catch (error) {}
	};

	const openDropdownDeductions = async () => {
		if (!userProfile?.id) return;
		try {
			await sendEvent({
				user_id: userProfile.id,
				event: ANALYTICS_EVENTS.APPROVAL_DECLARATIONS,
				source: 'web',
				event_payload: {
					subsection: EVENT_TYPES.APPROVAL_DECLARATIONS_OPEN_DROPDOWN_PERSONAL_DEDUCTIONS,
				},
			});
			trackEvent(EVENT_TYPES.APPROVAL_DECLARATIONS_OPEN_DROPDOWN_PERSONAL_DEDUCTIONS);
		} catch (error) {}
	};

	const abandonedFlowStepExpenses = async () => {
		if (!userProfile?.id) return;
		try {
			await sendEvent({
				user_id: userProfile.id,
				event: ANALYTICS_EVENTS.APPROVAL_DECLARATIONS,
				source: 'web',
				event_payload: {
					subsection: EVENT_TYPES.APPROVAL_DECLARATIONS_ABANDONED_FLOW_STEP_EXPENSES,
				},
			});
			trackEvent(EVENT_TYPES.APPROVAL_DECLARATIONS_ABANDONED_FLOW_STEP_EXPENSES);
		} catch (error) {}
	};

	const abandonedFlowStepSummary = async () => {
		if (!userProfile?.id) return;
		try {
			await sendEvent({
				user_id: userProfile.id,
				event: ANALYTICS_EVENTS.APPROVAL_DECLARATIONS,
				source: 'web',
				event_payload: {
					subsection: EVENT_TYPES.APPROVAL_DECLARATIONS_ABANDONED_FLOW_STEP_SUMMARY,
				},
			});
			trackEvent(EVENT_TYPES.APPROVAL_DECLARATIONS_ABANDONED_FLOW_STEP_SUMMARY);
		} catch (error) {}
	};

	const reviewDeclaration = async () => {
		if (!userProfile?.id) return;
		try {
			await sendEvent({
				user_id: userProfile.id,
				event: ANALYTICS_EVENTS.APPROVAL_DECLARATIONS,
				source: 'web',
				event_payload: {
					subsection: EVENT_TYPES.APPROVAL_DECLARATIONS_REVIEW_DECLARATION,
				},
			});
			trackEvent(EVENT_TYPES.APPROVAL_DECLARATIONS_REVIEW_DECLARATION);
		} catch (error) {}
	};

	const sendReviewDeclaration = async () => {
		if (!userProfile?.id) return;
		try {
			await sendEvent({
				user_id: userProfile.id,
				event: ANALYTICS_EVENTS.APPROVAL_DECLARATIONS,
				source: 'web',
				event_payload: {
					subsection: EVENT_TYPES.APPROVAL_DECLARATIONS_SEND_REVIEW,
				},
			});
			trackEvent(EVENT_TYPES.APPROVAL_DECLARATIONS_SEND_REVIEW);
		} catch (error) {}
	};
	

	useEffect(() => {
		if (
			declarationDetail?.precalculate_details?.[0]?.month &&
			declarationDetail?.precalculate_details?.[0]?.year
		) {
			getMonthPreviewData(
				declarationDetail?.precalculate_details?.[0]?.month,
				declarationDetail?.precalculate_details?.[0]?.year,
			);
		}
	}, [
		declarationDetail?.precalculate_details?.[0]?.month,
		declarationDetail?.precalculate_details?.[0]?.year,
	]);

	useEffect(() => {
		if (fftId) {
			getDeclarationDetail(Number(fftId));
			getDeclarationDetailFFT(Number(fftId));
		}
	}, [fftId]);

	return (
		<div className='w-full p-3 md:p-12 inline-flex flex-col justify-start items-center gap-6 mt-[16px]'>
			<div className='w-full flex justify-start items-start gap-2'>
				<div>
					<CaretCircleLeft
						weight='fill'
						color='var(--h-primary)'
						width={32}
						height={32}
						className='cursor-pointer hidden lg:block'
						onClick={() => backApprovalDeclarations()}
					/>
					<CaretCircleLeft
						weight='fill'
						color='var(--h-primary)'
						width={24}
						height={24}
						className='cursor-pointer block lg:hidden'
						onClick={() => backApprovalDeclarations()}
					/>
				</div>
				<div className='flex flex-col justify-start items-start'>
					<Text
						size='body-3'
						weight='bold'
					>
						{getCompleteMonth(
							Number(declarationDetail?.precalculate_details?.[0]?.month),
						)}{' '}
						{declarationDetail?.precalculate_details?.[0]?.year}
					</Text>
				</div>
			</div>

			<StepsAnnual
				numberOfSteps={3}
				currentStep={currentStep}
				setCurrentStep={handleStepClick}
				isDisabled={!incomes?.retentionIncome}
			/>

			<div className='w-full flex flex-col gap-4 md:max-w-[700px] md:justify-center'>
				<div className='self-stretch p-4 bg-white rounded-tr-lg rounded-bl-lg rounded-br-lg rounded-tl-lg flex flex-col justify-start items-start gap-4'>
					{renderStepContent()}
				</div>

				{currentStep === 2 &&
					monthPreviewData?.deductions?.items?.length > 0 && (
						<div className='self-stretch p-4 bg-white rounded-tr-lg rounded-bl-lg rounded-br-lg rounded-tl-lg flex flex-col justify-start items-start gap-4'>
							<div className='self-stretch flex-col inline-flex justify-start items-start gap-0'>
								<Text
									size='body-2'
									weight='regular'
								>
									Deducciones personales
								</Text>
								<Text
									size='body-3'
									weight='light'
								>
									Tus deducciones personales que se aplicarán cuando presentemos
									tu declaración anual.
								</Text>
							</div>
							<div className='w-full mt-2'>
								<button
									onClick={() => {
										setIsOpenPersonales(!isOpenPersonales);
										openDropdownDeductions();
									}}
									className='w-full flex items-center justify-between p-3 rounded-lg outline outline-1 outline-[#e5e5e5]'
								>
									<div className='flex items-center gap-2'>
										<div className='w-1.5 h-1.5 bg-h-primary rounded-full' />
										<Text
											size='caption'
											weight='regular'
										>
											Deducciones personales para tu anual
										</Text>
									</div>
									<CaretDown
										size={16}
										color='var(--h-primary)'
										className={`transform transition-transform ${isOpenPersonales ? 'rotate-180' : ''}`}
									/>
								</button>

								{isOpenPersonales && (
									<div className='mt-2'>
										{monthPreviewData?.deductions?.items?.map(
											(item: any, index: number) => (
												<div
													key={index}
													className='rounded-lg p-2.5 mb-2 w-full outline outline-1 outline-[#e5e5e5]'
												>
													<div className='flex items-center gap-2'>
														<div>
															<GraduationCap
																size={16}
																color='var(--h-primary)'
																weight='duotone'
															/>
														</div>
														<div className='w-full flex flex-col gap-[8px]'>
															<div className='flex items-center justify-between'>
																<Text
																	size='caption'
																	weight='regular'
																>
																	{item.deduction_type || 'Sin descripción'}
																</Text>
																<Text
																	size='caption'
																	weight='regular'
																>
																	$
																	{Math.trunc(item.amount).toLocaleString(
																		'es-MX',
																	) || '0'}
																</Text>
															</div>

															<Text
																size='mini-1'
																weight='light'
															>
																{item.description || 'Fecha no disponible'}
															</Text>
														</div>
													</div>
												</div>
											),
										)}
									</div>
								)}
							</div>
						</div>
					)}

				<div className='flex justify-between w-full gap-4'>
					{currentStep > 1 && (
						<PrimaryButton
							label='Volver'
							variant='outline'
							onClick={handleBack}
							labelColor='primary'
							beforeIcon={
								<CaretLeft
									weight='regular'
									size={24}
								/>
							}
						/>
					)}

					<PrimaryButton
						disabled={!incomes?.retentionIncome}
						label={currentStep === 3 ? 'Enviar a revisión' : 'Continuar'}
						variant={currentStep === 3 ? 'outline' : 'primary'}
						onClick={currentStep === 3 ? handleSendToReview : handleNext}
						icon={
							currentStep !== 3 ? (
								<CaretRight
									weight='regular'
									size={24}
								/>
							) : (
								<Binoculars
									weight='regular'
									size={24}
								/>
							)
						}
					/>
				</div>
			</div>
			{/* Modal para enviar a revisión */}
			<Modal
				sx='w-full max-w-[450px]'
				open={showReviewModal}
				onClose={() => setShowReviewModal(false)}
			>
				<div className='flex flex-col items-start justify-start gap-6 p-4'>
					<Text
						size='body-1'
						weight='medium'
					>
						Enviar a revisión
					</Text>

					<div className='w-full'>
						<Text
							size='body-3'
							weight='regular'
							sx='mb-4'
						>
							Selecciona la razón por la que deseas enviar a revisión esta
							declaración:
						</Text>

						<div className='flex flex-col gap-3 w-full mb-4'>
							{[
								'Inconsistencias en mis ingresos y retenciones',
								'Inconsistencias en mis deducciones',
								'Otros',
							].map((option) => (
								<div
									key={option}
									className={`flex items-center p-3 rounded-lg border cursor-pointer 
										${
											selectedOption === option
												? 'border-h-primary bg-h-primary-100'
												: 'border-h-neutral-90'
										}`}
									onClick={() => setSelectedOption(option)}
								>
									<div
										className={`w-4 h-4 rounded-full mr-3 
											${
												selectedOption === option
													? 'border-4 border-h-primary'
													: 'border border-h-neutral-70'
											}`}
									/>
									<Text
										size='body-3'
										weight='regular'
									>
										{option}
									</Text>
								</div>
							))}
						</div>

						<div className='w-full mb-4'>
							<Text
								size='body-3'
								weight='regular'
								sx='mb-2'
							>
								Añade detalles sobre tu solicitud:
							</Text>
							<textarea
								className={`w-full p-3 border rounded-lg resize-none h-32 bg-white
									${validationError && !reasonText ? 'border-h-red-50' : 'border-h-neutral-90'}`}
								value={reasonText}
								onChange={(e) => setReasonText(e.target.value)}
								placeholder='Escribe aquí los detalles de tu solicitud...'
							/>
							{validationError && !reasonText && (
								<Text
									size='mini-1'
									weight='regular'
									sx='mt-1'
								>
									Este campo es obligatorio
								</Text>
							)}
						</div>

						<div className='flex justify-end mt-4'>
							<PrimaryButton
								label='Enviar a revisión'
								variant='primary'
								onClick={handleModalSubmit}
								disabled={!selectedOption || !reasonText.trim()}
							/>
						</div>
					</div>
				</div>
			</Modal>

			{/* Modal de confirmación para salir del flujo */}
			<Modal
				sx='w-[94%] !max-w-md'
				open={blocker.state === 'blocked'}
				onClose={() => blocker.reset && blocker.reset()}
			>
				<div className='flex flex-col items-center justify-center gap-6 p-4'>
					<Warning
						color='var(--h-error-50)'
						size={60}
						weight='duotone'
					/>
					<Text
						size='body-1'
						weight='medium'
						sx='text-center'
					>
						¿Deseas salir del flujo de aprobación?
					</Text>
					<Text
						size='body-3'
						weight='light'
						sx='text-center'
					>
						Si sales del flujo, deberás volver a iniciar el proceso de
						aprobación de tu declaración.
					</Text>
					<div className='flex flex-col w-full'>
						<PrimaryButton
							label='No, continuar'
							onClick={() => blocker.reset && blocker.reset()}
						/>
						<PrimaryButton
							label='Sí, salir del flujo'
							variant='secondary'
							onClick={() => {
								blocker.proceed && blocker.proceed();
								backApprovalDeclarations();
								if (currentStep === 2) {
									abandonedFlowStepExpenses();
								} else if (currentStep === 3) {
									abandonedFlowStepSummary();
								}
							}}
							sx='mt-2'
						/>
					</div>
				</div>
			</Modal>
		</div>
	);
};

export default ApprovalDeclarationsSteps;
